<script setup lang="ts">
import AppState, {
  getEquipmentsInLocation,
  getTargetTimezone,
  isCreateCalendarDisabled,
} from '@/states/app'
import {
  CalendarCheck01Icon,
  Pencil02Icon,
  PlusIcon,
  Toggle03LeftIcon,
  Toggle03RightIcon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIActionDropdown,
  UIButton,
  UIEmpty,
  UITable,
  UITag,
  UITooltip,
  renderIcon,
  useNotification,
} from '@gohighlevel/ghl-ui'

import { h, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ResourceList } from '../../class/pages/ResourceList'
import { CalendarResourceService } from '../../class/services/CalendarResourceService'
import {
  CALENDAR_RESOURCE_TYPES,
  CalendarResource,
} from '../../models/calendar_resource'
import CreateEquipmentModal from './CreateEquipmentModal.vue'

import DeleteConfirmation from '@/components/common/DeleteConfirmation.vue'
import GeneralConfirmation from '@/components/common/GeneralConfirmation.vue'
import moment from 'moment-timezone'
import SearchBox from '../common/SearchBox.vue'

const { t } = useI18n()

const tableActionsOptionsForActiveEquipments = [
  {
    label: t('settings.calendar.edit'),
    key: 'edit',
    icon: renderIcon(Pencil02Icon),
  },
  {
    label: t('settings.calendar.equipments_disable'),
    key: 'disable',
    icon: renderIcon(Toggle03RightIcon),
  },
  {
    label: t('settings.calendar.delete_group'),
    key: 'delete_equipment',
    icon: renderIcon(Trash01Icon),
  },
]

const tableActionsOptionsForDisabledEquipments = [
  ...tableActionsOptionsForActiveEquipments,
]
tableActionsOptionsForDisabledEquipments[1] = {
  label: t('settings.calendar.equipments_activate'),
  key: 'disable',
  icon: renderIcon(Toggle03LeftIcon),
}

const targetTimezone = getTargetTimezone()

const columns = [
  {
    title: t('settings.calendar.equipment_name'),
    key: 'name',
  },
  {
    title: t('settings.calendar.equipment_quantity'),
    key: 'quantity',
  },
  {
    title: t('settings.calendar.calendar_list'),
    key: 'calendars_tags',
    render(row) {
      const tags: any = []
      const calendarIds = row.calendarIds
      if (!calendarIds?.length) {
        return
      }
      const activeCalendars = calendarIds.filter(
        e => !equipmentList.isCalendarDeleted(e)
      )
      for (let i = 0; i < 2; i++) {
        if (activeCalendars[i]) {
          const calendarName = equipmentList.getCalName(activeCalendars[i])
          const truncatedName =
            calendarName.length > 15
              ? calendarName.substring(0, 15) + '...'
              : calendarName

          tags.push(
            h(
              UITooltip,
              { placement: 'top' },
              {
                trigger: () =>
                  h(
                    UITag,
                    {
                      type: 'info',
                      id: activeCalendars[i],
                      class: 'cursor-pointer',
                    },
                    truncatedName
                  ),
                default: () =>
                  h('div', { class: 'max-w-[280px] w-auto' }, calendarName),
              }
            )
          )
        }
      }
      if (activeCalendars.length > 2) {
        const additionalCalendars = activeCalendars
          .slice(2)
          .map(id => equipmentList.getCalName(id))

        tags.push(
          h(
            UITooltip,
            { placement: 'top' },
            {
              trigger: () =>
                h(
                  UITag,
                  {
                    id: `count`,
                    class: 'cursor-pointer',
                  },
                  `+${activeCalendars.length - 2}`
                ),
              default: () =>
                h(
                  'div',
                  {
                    class: 'max-w-[180px] mb-[5px]',
                    style: { fontFamily: 'Inter !important' },
                  },
                  additionalCalendars.map((name, index) =>
                    h(
                      'div',
                      { class: 'max-w-[280px] w-auto break-words' },
                      index + 1 + '. ' + name
                    )
                  )
                ),
            }
          )
        )
      }
      return h(
        'div',
        {
          style: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
          },
        },
        tags
      )
    },
  },
  {
    title: t('settings.calendar.status'),
    key: 'isActive',
    render(equipment) {
      return h('div', [
        h(
          UITag,
          {
            type: equipment.isActive ? 'success' : 'error',
            bordered: false,
            size: 'medium',
          },
          equipment.isActive
            ? t('settings.calendar.active')
            : t('settings.calendar.inactive')
        ),
      ])
    },
  },
  {
    title: t('settings.calendar.date_updated'),
    key: 'dateUpdated',
    render(equipment) {
      return h('div', { class: 'flex flex-col gap-1 items-start' }, [
        h(
          'div',
          {
            class: 'm-0 text-[13px] font-normal text-gray-600',
          },
          moment(equipment.updatedAt).tz(targetTimezone).format('MMM DD YYYY')
        ),
        h(
          'h6',
          {
            class: 'm-0 text-[13px] font-normal text-gray-400',
          },
          `${moment(equipment.updatedAt).tz(targetTimezone).format('hh:mm A')}`
        ),
      ])
    },
  },
  {
    title: t('settings.calendar.action_dropdown'),
    key: 'actions',
    render(row) {
      // GHLPRD-14429 — manage-calendars (calendars.write) gates equipment mutations.
      if (isCreateCalendarDisabled.value) return null
      return h(
        UIActionDropdown,
        {
          options: row.isActive
            ? tableActionsOptionsForActiveEquipments.filter(
                x => x.key !== 'rearrange_calendars' || row.nosOfCalendars > 0
              )
            : tableActionsOptionsForDisabledEquipments.filter(
                x => x.key !== 'rearrange_calendars' || row.nosOfCalendars > 0
              ),
          onSelect: val => dropDownActionChange(val, row),
          id: 'equipment-' + row._id,
        },
        {}
      )
    },
  },
]

const selectedEquipment = ref()
const isProcessing = ref(false)
const showDeleteConfirmationModal = ref(false)
const showNewEquipmentModal = ref(false)
const notification = useNotification()
const equipmentList = new ResourceList(CALENDAR_RESOURCE_TYPES.EQUIPMENTS)
const showDisableConfirmationModal = ref(false)
const equipmentEditPayload = ref()

onMounted(async () => {
  await getEquipmentsInLocation()
})

const dropDownActionChange = (action, equipment) => {
  selectedEquipment.value = equipment
  if (action === 'edit') {
    openEquipmentModal(equipment)
  } else if (action === 'disable') {
    showDisableConfirmationModal.value = true
  } else if (action === 'delete_equipment') {
    showDeleteConfirmationModal.value = true
  }
}

const closeModal = () => {
  showDisableConfirmationModal.value = false
  showNewEquipmentModal.value = false
  showDeleteConfirmationModal.value = false
  isProcessing.value = false
}

const openEquipmentModal = (equipment: CalendarResource | null) => {
  showNewEquipmentModal.value = true
  equipmentEditPayload.value = equipment
}

const openNewEquipmentModal = () => {
  showNewEquipmentModal.value = true
  equipmentEditPayload.value = null
}

const showInfoNotification = (message: string) => {
  notification.info({
    content: message,
    duration: 1 * 1000,
  })
}

// API calls start
const runPostApiCallsCleanUps = async () => {
  closeModal()
  await getEquipmentsInLocation(true)
}

const deleteEquipment = async () => {
  if (!selectedEquipment.value) {
    return
  }
  isProcessing.value = true
  await CalendarResourceService.delete(
    selectedEquipment.value._id,
    CALENDAR_RESOURCE_TYPES.EQUIPMENTS
  )
  showInfoNotification('Equipment deleted')
  await runPostApiCallsCleanUps()
}

const disableEquipment = async () => {
  if (!selectedEquipment.value) {
    return
  }
  isProcessing.value = true
  await CalendarResourceService.update(
    selectedEquipment.value._id,
    { isActive: !selectedEquipment.value.isActive },
    CALENDAR_RESOURCE_TYPES.EQUIPMENTS
  )
  showInfoNotification(
    `Equipment ${selectedEquipment.value.isActive ? 'Disabled' : 'Activated'}`
  )
  await runPostApiCallsCleanUps()
}

const onSearch = (value: string) => {
  equipmentList.filters.value.searchText = value
  equipmentList.filters.value.page = 1
}
// API calls end
</script>
<template>
  <div id="equipmentsListContainer">
    <CreateEquipmentModal
      :show="showNewEquipmentModal"
      :edit-payload="equipmentEditPayload"
      @createdRecord="runPostApiCallsCleanUps"
      @closeModal="closeModal"
    />
    <UITable
      id="equipment-list-table"
      :columns="columns"
      :data="equipmentList.resourceList"
      :page="equipmentList.filters.value.page"
      :page-count="equipmentList.totalPages"
      :loading="AppState.isLoading || AppState.loadingAssets.equipments"
      :table-layout="'fixed'"
      :enable-text-search="false"
      transparent
      @update:page="x => (equipmentList.filters.value.page = x)"
    >
      <template #empty>
        <UIEmpty
          v-if="equipmentList.allResources?.length === 0"
          id="empty-equipments-title"
          :title="$t('settings.calendar.no_equipments_in_location_title')"
          :description="$t('settings.calendar.no_equipments_in_location')"
          :positive-text="$t('settings.calendar.add_new_equipment')"
          :icon="CalendarCheck01Icon"
          :positive-icon="PlusIcon"
          @positiveClick="openEquipmentModal"
        />

        <UIEmpty
          v-else
          id="empty-equipments-description"
          :title="$t('settings.calendar.no_equipments_in_location_title')"
          :description="$t('settings.calendar.no_equipments_found')"
        />
      </template>
      <template #filter-left>
        <div class="text-[18px] font-semibold text-gray-900">
          {{ t('settings.calendar.equipments') }}
        </div>
      </template>
      <template #filter-right-end>
        <SearchBox
          :search-text="equipmentList.filters.value.searchText"
          :on-search="onSearch"
          :placeholder="t('settings.calendar.equipment_name')"
        />
        <UIButton
          v-if="!isCreateCalendarDisabled"
          id="calendar-settings-equipments-headers-btn-primary"
          type="primary"
          size="small"
          @click="
            () => {
              openEquipmentModal(null)
            }
          "
        >
          <div class="flex flex-row items-center gap-2">
            <PlusIcon class="h-5 w-5 text-white" />
            <div class="font-normal">
              {{ $t('settings.calendar.create_equipments') }}
            </div>
          </div>
        </UIButton>
      </template>
    </UITable>
    <DeleteConfirmation
      v-model:show="showDeleteConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="$t('settings.calendar.delete_this_equipment')"
      @onConfirm="deleteEquipment"
      @onModalClose="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{ $t('settings.calendar.delete_equipment_confirmation') }}
        </p>
      </template>
    </DeleteConfirmation>

    <GeneralConfirmation
      v-model:show="showDisableConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="
        $t('settings.calendar.disable_equipment_title', {
          status: selectedEquipment?.isActive ? 'Disable' : 'Activate',
        })
      "
      @onConfirm="disableEquipment"
      @onModalClose="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{
            t('settings.calendar.equipments_status_message', {
              status: selectedEquipment?.isActive ? 'Disable' : 'Activate',
            })
          }}
        </p>
        <p class="text-sm leading-5 text-gray-500">
          {{ t('settings.calendar.calendar_resource_disclaimer') }}
        </p>
        <br />
        <p
          v-if="selectedEquipment?.isActive"
          class="text-sm leading-5 text-gray-500"
        >
          {{ t('settings.calendar.equipment_disclaimer') }}
        </p>
      </template>
    </GeneralConfirmation>
  </div>
</template>

<style lang="scss">
#equipmentsListContainer {
  .table-container .n-data-table .n-data-table-td {
    padding: 10px 24px !important;
  }
}
</style>
